import { matchesPathname } from "@/lib/pathname";

type ResolveRedirectPathParams = {
  origin: string;
  explicitReturnTo?: string | null;
  from?: string | null;
  callbackUrl?: string | null;
  currentPath?: string | null;
};

const NON_RETURN_PATHS = ["/login", "/logout", "/onboarding"] as const;
const STRUCTURALLY_ENCODED_URL_PREFIX = /^(?:%2f|https?%3a)/i;

type RedirectSearchParams = {
  get(name: string): string | null;
};

function safeRedirectParam(
  searchParams: RedirectSearchParams,
  param: "callbackUrl" | "from",
  origin: string
): string | null {
  return toSafePostAuthDestination(searchParams.get(param), origin);
}

export function safePostAuthSearchParams(
  searchParams: RedirectSearchParams,
  origin: string
) {
  return {
    from: safeRedirectParam(searchParams, "from", origin),
    callbackUrl: safeRedirectParam(searchParams, "callbackUrl", origin),
  };
}

export function getFirstQueryParam(
  value: string | string[] | undefined
): string | undefined {
  if (Array.isArray(value)) {
    return value[0];
  }

  return value;
}

function decodeStructurallyEncodedUrl(value: string) {
  if (!STRUCTURALLY_ENCODED_URL_PREFIX.test(value)) {
    return value;
  }

  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export function toSafeSameOriginUrl(
  value: string | null | undefined,
  origin: string
): string | null {
  if (!value) {
    return null;
  }

  const decodedValue = decodeStructurallyEncodedUrl(value);
  const isAbsoluteUrl =
    decodedValue.startsWith("http://") || decodedValue.startsWith("https://");
  const normalizedValue =
    decodedValue.startsWith("/") || isAbsoluteUrl
      ? decodedValue
      : `/${decodedValue}`;

  try {
    const parsed = new URL(normalizedValue, origin);
    if (parsed.origin !== origin) {
      return null;
    }

    return parsed.toString();
  } catch {
    return null;
  }
}

function isNonReturnPath(pathname: string, search: string, hash: string) {
  if (pathname === "/" && !search && !hash) {
    return true;
  }

  return NON_RETURN_PATHS.some((authPath) =>
    matchesPathname(pathname, authPath)
  );
}

export function toSafeRedirectPath(
  value: string | null | undefined,
  origin: string
): string | null {
  const safeUrl = toSafeSameOriginUrl(value, origin);
  if (!safeUrl) {
    return null;
  }

  const parsed = new URL(safeUrl);
  if (isNonReturnPath(parsed.pathname, parsed.search, parsed.hash)) {
    return null;
  }

  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

export function toSafePostAuthDestination(
  value: string | null | undefined,
  origin: string
): string | null {
  const directDestination = toSafeRedirectPath(value, origin);
  if (directDestination) {
    return directDestination;
  }

  const safeUrl = toSafeSameOriginUrl(value, origin);
  if (!safeUrl) {
    return null;
  }

  const authEnvelope = new URL(safeUrl);
  if (authEnvelope.pathname !== "/login") {
    return null;
  }

  for (const param of ["from", "callbackUrl"] as const) {
    const nestedDestination = toSafeRedirectPath(
      authEnvelope.searchParams.get(param),
      origin
    );
    if (nestedDestination) {
      return nestedDestination;
    }
  }

  return null;
}

export function getCurrentPathFromLocation(href?: string): string {
  const locationHref =
    href ??
    (typeof window !== "undefined"
      ? window.location.href
      : "http://localhost/");

  const locationUrl = new URL(locationHref);
  return `${locationUrl.pathname}${locationUrl.search}${locationUrl.hash}`;
}

function resolveReturnPath(params: ResolveRedirectPathParams): string | null {
  const { origin, explicitReturnTo, from, callbackUrl, currentPath } = params;

  for (const candidate of [explicitReturnTo, from, callbackUrl, currentPath]) {
    const safePath = toSafePostAuthDestination(candidate, origin);
    if (safePath) {
      return safePath;
    }
  }

  return null;
}

export function getClientPostAuthDestination(params?: {
  explicitReturnTo?: string;
}): string {
  const locationUrl = new URL(window.location.href);
  const currentPath = getCurrentPathFromLocation(locationUrl.href);
  const { from, callbackUrl } = safePostAuthSearchParams(
    locationUrl.searchParams,
    locationUrl.origin
  );

  const returnPath = resolveReturnPath({
    origin: locationUrl.origin,
    explicitReturnTo: params?.explicitReturnTo,
    from,
    callbackUrl,
    currentPath,
  });

  return returnPath ?? "/";
}

export function getReturnPathFromSearchParams(
  requestUrl: string,
  searchParams: RedirectSearchParams
): string | null {
  const origin = new URL(requestUrl).origin;
  const { from, callbackUrl } = safePostAuthSearchParams(searchParams, origin);

  return resolveReturnPath({
    origin,
    from,
    callbackUrl,
  });
}

export function buildOnboardingRedirectUrl(
  requestUrl: string,
  returnPath: string | null
): string {
  const onboarding = new URL("/onboarding", requestUrl);
  const safePath = returnPath
    ? toSafeRedirectPath(returnPath, new URL(requestUrl).origin)
    : null;

  if (safePath) {
    onboarding.searchParams.set("from", safePath);
  }

  return `${onboarding.pathname}${onboarding.search}`;
}

export function buildLoginRedirectUrl(requestUrl: string): string {
  const request = new URL(requestUrl);
  const login = new URL("/login", request);
  const returnPath = toSafeRedirectPath(
    `${request.pathname}${request.search}`,
    request.origin
  );

  if (returnPath) {
    login.searchParams.set("from", returnPath);
  }

  return `${login.pathname}${login.search}`;
}
